import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  PanelCard,
  Tabs,
  Spinner,
  Alert,
  Button,
  TextField,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  type MailingChannel,
  type MailingSummary,
  type SubscriberItem,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';

/**
 * Mailing-list console (mailinglist:read) — the marketing-subscriber audiences
 * derived from per-user consent (tblusercommunicationconsents). Summary counts
 * + a per-channel (email/sms/push) subscriber list with search + CSV export.
 * Always excludes doNotSell + non-active users. Read-only.
 */

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <PanelCard as="div" accent={accent} sx={{ minWidth: 150, flex: 1 }}>
      <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
        {value.toLocaleString()}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </PanelCard>
  );
}

function downloadCsv(channel: MailingChannel, rows: SubscriberItem[]) {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = 'email,name,opted_in_at';
  const body = rows
    .map((r) => [esc(r.email ?? ''), esc(r.name ?? ''), esc(r.optInAt ?? '')].join(','))
    .join('\n');
  const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mailing-list-${channel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MailingList() {
  const caps = useCapabilities();
  const canRead = caps.has('mailinglist:read');

  const [summary, setSummary] = useState<MailingSummary | null>(null);
  const [channel, setChannel] = useState<MailingChannel>('email');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<SubscriberItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await adminApiClient.mailingListSummary());
    } catch {
      /* summary is best-effort; the list error surfaces below */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listSubscribers({ channel, q: query || undefined });
      setItems(res.items);
      setCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [channel, query]);

  useEffect(() => {
    if (canRead) void loadSummary();
  }, [canRead, loadSummary]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  const loadMore = async () => {
    if (!cursor) return;
    try {
      const res = await adminApiClient.listSubscribers({
        channel,
        q: query || undefined,
        after: cursor,
      });
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    }
  };

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canRead)
    return <Alert severity="error">You don’t have permission to view the mailing list.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Mailing list
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Marketing subscribers by channel, from user consent. Suppressed: do-not-sell + inactive accounts.
      </Typography>

      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Stat label="Active accounts" value={summary.total} />
          <Stat label="Email opt-in" value={summary.email} accent="#2E9E3A" />
          <Stat label="SMS opt-in" value={summary.sms} accent="#2E9E3A" />
          <Stat label="Push opt-in" value={summary.push} accent="#2E9E3A" />
          <Stat label="Do-not-sell" value={summary.doNotSell} accent="#C0392B" />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Tabs
          ariaLabel="Subscriber channel"
          value={channel}
          onChange={(_e, v) => setChannel(v as MailingChannel)}
          tabs={[
            { value: 'email', label: 'Email' },
            { value: 'sms', label: 'SMS' },
            { value: 'push', label: 'Push' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          label="Search"
          ariaLabel="Search subscribers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          multiline={false}
          sx={{ width: 220 }}
        />
        <Button
          ariaLabel="Apply search"
          buttonText="Search"
          variant="outlined"
          size="small"
          sx={NEUTRAL_BTN}
          onClick={() => setQuery(search.trim())}
        />
        <Button
          ariaLabel="Export CSV"
          buttonText="Export CSV"
          variant="contained"
          size="small"
          onClick={() => downloadCsv(channel, items)}
          disabled={items.length === 0}
        />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : items.length === 0 ? (
        <EmptyState icon="messages" title="No subscribers" body="No subscribers match this channel/filter." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((s) => (
            <Paper key={s.glpUserId} sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.email ?? '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.name ?? ''}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {s.optInAt ? new Date(s.optInAt).toLocaleDateString() : ''}
              </Typography>
            </Paper>
          ))}
          {cursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button ariaLabel="Load more" buttonText="Load more" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={loadMore} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
